/**
 * EventDraftCard — the typed card for `propose_events_from_sources`
 * (ChatMessage.TOOL_CARDS; backend allowlist agent_types.RENDERED_TOOLS).
 *
 * Arc read source documents/media and proposes chronology events (quote-
 * verified). The user reviews the list — edit a title, clear a date they
 * disagree with, uncheck any to skip — and adds them one at a time (check
 * one) or all at once. "Add" goes through the build lane's context →
 * deterministic /entries/from-sources endpoint, which re-verifies. Arc never
 * writes entries. Outside the build lane the card renders read-only.
 */
import React, { useMemo, useState } from 'react';

import { useChronologyBuild } from './chronologyBuildContext';

const S = {
  card: { marginTop: 8, border: '1px solid #d9cdf5', borderRadius: 8, background: '#faf8ff', overflow: 'hidden', fontSize: 13 },
  head: { padding: '8px 12px', background: '#efe8fd', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 },
  hint: { fontSize: 12, color: '#8a7fae', fontWeight: 400 },
  row: { display: 'flex', gap: 8, padding: '8px 12px', borderTop: '1px solid #eee6fb', alignItems: 'flex-start' },
  main: { flex: 1, minWidth: 0 },
  titleInput: { width: '100%', fontSize: 13, fontWeight: 600, border: '1px solid transparent', borderRadius: 4, padding: '2px 4px', background: 'transparent' },
  meta: { fontSize: 12, color: '#667', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  dateBadge: { fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#e8f0ff', color: '#1a4fc0', display: 'inline-flex', gap: 5, alignItems: 'center' },
  undated: { fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#f0f0f4', color: '#778' },
  clearDate: { cursor: 'pointer', color: '#8a7fae', fontWeight: 700 },
  chip: { fontSize: 11, padding: '1px 6px', borderRadius: 8, background: '#eef2fb', color: '#456' },
  quote: { fontSize: 12, color: '#556', fontStyle: 'italic', marginTop: 3 },
  srcLink: { color: '#2f6df6', textDecoration: 'none', fontSize: 12 },
  added: { fontSize: 12, color: '#2c6e49', fontWeight: 600, whiteSpace: 'nowrap', paddingTop: 2 },
  actions: { display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid #eee6fb', background: '#fdfcff', flexWrap: 'wrap' },
  btnPrimary: { padding: '5px 12px', borderRadius: 6, border: '1px solid #5b3fa8', background: '#5b3fa8', color: '#fff', cursor: 'pointer', fontSize: 13 },
  btn: { padding: '5px 12px', borderRadius: 6, border: '1px solid #c7ccd6', background: '#fff', cursor: 'pointer', fontSize: 13 },
  err: { color: '#c0392b', fontSize: 12 },
};

function fmtDate(iso, dateOnly) {
  if (!iso) return null;
  const d = String(iso).slice(0, 10);
  if (dateOnly) return d;
  return `${d} ${String(iso).slice(11, 16)}`;
}

export default function EventDraftCard({ result }) {
  const build = useChronologyBuild();
  const cardKey = useMemo(() => JSON.stringify(result?.events || []), [result]);
  // Per-event editable state (title override, date cleared) + selection.
  const [edits, setEdits] = useState({});
  const [checked, setChecked] = useState(() => {
    const init = {};
    (result?.events || []).forEach((_, i) => { init[i] = true; });
    return init;
  });
  const [addedIdx, setAddedIdx] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const events = result?.events || [];
  if (!events.length) {
    return (
      <div style={S.card}>
        <div style={S.head}>✨ No events could be drafted</div>
        <div style={{ padding: '8px 12px', color: '#778' }}>
          {result?.dropped
            ? `${result.dropped} proposed event${result.dropped === 1 ? '' : 's'} couldn’t be verified against the sources and ${result.dropped === 1 ? 'was' : 'were'} dropped.`
            : 'The sources didn’t support any events.'}
        </div>
      </div>
    );
  }

  const canAdd = !!build && build.chronologyId === result.chronology_id;
  const setEdit = (i, patch) => setEdits((e) => ({ ...e, [i]: { ...e[i], ...patch } }));

  // Build the payload event (flattened verified shape) with the user's edits.
  const eventPayload = (i) => {
    const ev = events[i];
    const ed = edits[i] || {};
    const out = { ...ev };
    if (ed.title != null) out.title = ed.title;
    if (ed.dateCleared) { delete out.date; delete out.date_only; delete out.date_quote; delete out.date_confidence; delete out.date_rationale; }
    return out;
  };

  const add = async (indices) => {
    if (!canAdd || adding) return;
    const fresh = indices.filter((i) => !addedIdx.has(i));
    if (!fresh.length) return;
    setAdding(true); setError('');
    try {
      await build.addEvents(fresh.map(eventPayload), `${cardKey}:${fresh.join(',')}`);
      setAddedIdx((s) => { const n = new Set(s); fresh.forEach((i) => n.add(i)); return n; });
      setChecked((c) => { const n = { ...c }; fresh.forEach((i) => { n[i] = false; }); return n; });
    } catch (e) {
      setError(e.message || 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  const selectedIdx = events.map((_, i) => i).filter((i) => checked[i] && !addedIdx.has(i));
  const remainingIdx = events.map((_, i) => i).filter((i) => !addedIdx.has(i));

  return (
    <div style={S.card}>
      <div style={S.head}>
        <span>✨ Proposed events from sources</span>
        {result.dropped > 0 && (
          <span style={S.hint}>({result.dropped} unverifiable dropped)</span>
        )}
      </div>
      {events.map((ev, i) => {
        const ed = edits[i] || {};
        const isAdded = addedIdx.has(i);
        const dateShown = !ed.dateCleared && ev.date;
        const doc = ev.source_document_id;
        return (
          <div key={i} style={{ ...S.row, opacity: isAdded ? 0.6 : 1 }}>
            {canAdd && !isAdded && (
              <input
                type="checkbox"
                checked={!!checked[i]}
                onChange={() => setChecked((c) => ({ ...c, [i]: !c[i] }))}
                style={{ marginTop: 4 }}
                title="Include this event when adding"
              />
            )}
            <div style={S.main}>
              {canAdd && !isAdded ? (
                <input
                  style={S.titleInput}
                  value={ed.title != null ? ed.title : (ev.title || '')}
                  onChange={(e) => setEdit(i, { title: e.target.value })}
                  onFocus={(e) => { e.target.style.border = '1px solid #c7ccd6'; e.target.style.background = '#fff'; }}
                  onBlur={(e) => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }}
                />
              ) : (
                <div style={{ fontWeight: 600, padding: '2px 4px' }}>{ed.title != null ? ed.title : ev.title}</div>
              )}
              {ev.description && <div style={{ marginTop: 2, padding: '0 4px' }}>{ev.description}</div>}
              <div style={S.meta}>
                {dateShown ? (
                  <span style={S.dateBadge}>
                    📅 {fmtDate(ev.date, ev.date_only)}
                    {ev.date_confidence && ev.date_confidence !== 'high' ? ` (${ev.date_confidence})` : ''}
                    {canAdd && !isAdded && (
                      <span style={S.clearDate} title="Add this event undated instead" onClick={() => setEdit(i, { dateCleared: true })}>✕</span>
                    )}
                  </span>
                ) : (
                  <span style={S.undated}>Undated{ev.date && ed.dateCleared ? ' (date removed)' : ''}</span>
                )}
                {(ev.participants || []).slice(0, 6).map((p, j) => (
                  <span key={j} style={S.chip}>{p}</span>
                ))}
                {doc && (
                  <a href={`?doc=${doc}`} style={S.srcLink}
                    onClick={(e) => { if (e.metaKey || e.ctrlKey || e.button !== 0) return; }}>
                    📄 source
                  </a>
                )}
              </div>
              {dateShown && ev.date_quote && <div style={S.quote}>date: “{ev.date_quote}”</div>}
              {ev.anchor_quote && <div style={S.quote}>“{ev.anchor_quote}”</div>}
            </div>
            {isAdded && <span style={S.added}>✓ Added</span>}
          </div>
        );
      })}
      <div style={S.actions}>
        {!canAdd ? (
          <span style={S.hint}>Open Build with Arc on the Chronology tab to add these.</span>
        ) : remainingIdx.length === 0 ? (
          <span style={S.added}>✓ All added — review them below as suggested entries.</span>
        ) : (
          <>
            <button style={S.btnPrimary} onClick={() => add(selectedIdx)} disabled={adding || !selectedIdx.length || !!build.busy}>
              {adding ? 'Adding…' : `Add selected (${selectedIdx.length})`}
            </button>
            <button style={S.btn} onClick={() => add(remainingIdx)} disabled={adding || !!build.busy}>
              Add all ({remainingIdx.length})
            </button>
            <span style={S.hint}>They land as suggested entries to review — check one to add it alone.</span>
          </>
        )}
        {error && <span style={S.err}>{error}</span>}
      </div>
    </div>
  );
}
